import React, { type ReactElement, type ReactNode, useMemo } from "react";
import { useCFormSectionStore } from "../CFormSection/cform-section-store";
import { useCFormStore } from "../cform-store";
import type {
  CFormItemBaseProps as BaseCFormItemBaseProps,
  CFormItemLayoutType,
} from "../types/base-types";
import { CFormAttributeCardItemLayout } from "./layouts/CFormAttributeCardItemLayout";
import { CFormLabelItemLayout } from "./layouts/CFormLabelltemLayout";

export type CFormItemProps = BaseCFormItemBaseProps;

export type CFormItemBaseProps = BaseCFormItemBaseProps & {
  readComponent: ReactNode;
  writeComponent?: ReactNode;
};

const layoutComponentMap: Record<
  CFormItemLayoutType,
  React.ComponentType<CFormItemBaseProps>
> = {
  card: CFormAttributeCardItemLayout,
  label: CFormLabelItemLayout,
};

/**
 * Card-based Form / Item 共通
 *
 * 基本的には CFormTextItem 等の実装済みコンポーネントを活用し、
 * カスタムが必要な場合にこのコンポーネントを直接活用します。
 *
 * これを用いた実装を追加する際は、必ず buildCFormItem を活用してください。
 */
export function CFormItemBase(props: CFormItemBaseProps) {
  const cFormStore = useCFormStore((store) => store, { allowNoContext: true });
  const cFormSectionId = useCFormSectionStore(
    (store) => store.state.sectionId,
    { allowNoContext: true },
  );

  // props + cFormStore のマージ
  const mergedProps = useMemo(() => {
    return {
      hideActionButtons:
        props.hideActionButtons ??
        cFormStore?.state.isHideItemActions({ itemId: props.id }),
      isEditing:
        props.isEditing ??
        cFormStore?.state.isItemEditing({
          sectionId: cFormSectionId ?? undefined,
          itemId: props.id,
        }),
      isPending: props.isPending ?? cFormStore?.state.isPending,
      onStartEdit:
        props.onStartEdit ??
        (() => cFormStore?.actions.startEditItem(props.id)),
      onCancel: props.onCancel ?? cFormStore?.actions.reset,
      onSave: props.onSave ?? cFormStore?.actions.save,
    };
  }, [props, cFormStore?.state, cFormStore?.actions, cFormSectionId]);

  const ItemLayoutComponent =
    layoutComponentMap[cFormStore?.state.itemLayout ?? "card"];

  return <ItemLayoutComponent {...props} {...mergedProps} />;
}

const ItemSymbol = Symbol("__CFormItemBase");

/**
 * CForm 内部で活用する Item コンポーネントを開発する際は、必ず活用してください。
 * これを経由することによって、自動的に共通プロパティが注入されるようになります。
 */
export function buildCFormItem<Props extends CFormItemProps>(
  component: (props: Props) => ReactElement,
) {
  Object.assign(component, { [ItemSymbol]: true });
  return component;
}

/**
 * Node が CFormItem であるかを判断する
 */
export function isCFormItem(
  node: ReactNode,
): node is ReactElement<CFormItemProps, typeof CFormItemBase> {
  const nodeType = React.isValidElement(node) ? (node?.type as object) : null;
  return nodeType != null && ItemSymbol in nodeType
    ? nodeType[ItemSymbol] === true
    : false;
}
